import { RouteUrl } from "@/configs/router/routes";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Input } from "@/components/shadcn/input";
import MaterialIcon from "@/components/general/common/MaterialIcon";
import MultiSelect from "./MultiSelect";
import { ExportToExcelButton } from "@/components/general/common/ExportToExcelButton";
import type { StatCategory as PlayerStatCategory } from "@/components/general/Scouting/ScoutingPlayersTable";
import type { StatCategory as GoalkeeperStatCategory } from "@/components/general/Scouting/ScoutingGoalkeepersTable";

export type ScoutingStatCategory = PlayerStatCategory | GoalkeeperStatCategory;

const navItems = [
    { to: RouteUrl.ScoutingPlayers, label: "Players Rating", shortLabel: "Players" },
    { to: RouteUrl.ScoutingGoalkeepers, label: "Goalkeepers Rating", shortLabel: "Goalkeepers" },
    { to: RouteUrl.ScoutingFollowed, label: "Followed Players", shortLabel: "Followed" },
];

interface ScoutingNavigationProps {
    statCategory?: ScoutingStatCategory;
    onStatCategoryChange?: (category: ScoutingStatCategory) => void;
    searchQuery?: string;
    onSearchQueryChange?: (q: string) => void;
    positionFilter?: string[];
    onPositionFilterChange?: (p: string[]) => void;
    teamFilter?: string[];
    onTeamFilterChange?: (t: string[]) => void;
    availablePositions?: string[];
    availableTeams?: string[];
    onExportToExcel?: () => Promise<{ data: Blob; contentDisposition?: string }>;
}

const ScoutingNavigation = ({
    statCategory = "overview",
    onStatCategoryChange,
    searchQuery = "",
    onSearchQueryChange,
    positionFilter = [],
    onPositionFilterChange,
    teamFilter = [],
    onTeamFilterChange,
    availablePositions = [],
    availableTeams = [],
    onExportToExcel,
}: ScoutingNavigationProps) => {
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const navigate = useNavigate();

    const isPlayersPage = pathname === RouteUrl.ScoutingPlayers;
    const isGoalkeepersPage = pathname === RouteUrl.ScoutingGoalkeepers;
    const isFollowedPage = pathname === RouteUrl.ScoutingFollowed;

    const getStatCategories = () => {
        if (isPlayersPage) {
            return [
                { id: "overview", label: "Overview" },
                { id: "skating", label: "Skating" },
                { id: "puck", label: "Puck Control" },
                { id: "actions", label: "Actions" },
                { id: "passing", label: "Passing" },
                { id: "advanced", label: "Advanced" },
            ];
        }
        if (isGoalkeepersPage) {
            return [
                { id: "overview", label: "Overview" },
                { id: "performance", label: "Performance" },
                { id: "passing", label: "Passing" },
                { id: "screens", label: "Screens" },
            ];
        }
        return [{ id: "overview", label: "Overview" }];
    };

    const statCategories = getStatCategories();
    const showFilters = (isPlayersPage || isGoalkeepersPage) && !isFollowedPage;

    return (
        <div className="flex flex-col gap-2 mb-1">
            <nav id="onboarding-scouting-nav" className="rounded-md p-1 bg-surface-container-low">
                <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                    {navItems.map((item) => {
                        const isActive = pathname === item.to;
                        return (
                            <button
                                key={item.to}
                                type="button"
                                className={`nav-tab ${isActive ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => {
                                    if (!isActive) {
                                        navigate({ to: item.to });
                                    }
                                }}
                            >
                                <span className="md:hidden">{item.shortLabel}</span>
                                <span className="hidden md:inline">{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>
            {showFilters && (
                <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
                    {onSearchQueryChange && (
                        <div id="onboarding-scouting-search" className="relative w-full md:w-auto">
                            <MaterialIcon
                                name="search"
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-lg pointer-events-none"
                            />
                            <Input
                                value={searchQuery}
                                onChange={(e) => onSearchQueryChange(e.target.value)}
                                placeholder="Search by name..."
                                className="pl-8 h-9 w-full md:w-44 bg-surface-container-highest border-0 text-sm"
                            />
                        </div>
                    )}
                    {isPlayersPage && onPositionFilterChange && availablePositions.length > 0 && (
                        <div id="onboarding-scouting-position-filter" className="w-full md:w-auto">
                            <MultiSelect
                                options={availablePositions}
                                selected={positionFilter}
                                onChange={onPositionFilterChange}
                                placeholder="All Positions"
                                icon="sports_hockey"
                                className="w-full md:w-auto"
                                buttonClassName="w-full md:w-auto"
                            />
                        </div>
                    )}
                    {onTeamFilterChange && availableTeams.length > 0 && (
                        <div id="onboarding-scouting-team-filter" className="w-full md:w-auto">
                            <MultiSelect
                                options={availableTeams}
                                selected={teamFilter}
                                onChange={onTeamFilterChange}
                                placeholder="All Teams"
                                icon="group"
                                className="w-full md:w-auto"
                                buttonClassName="w-full md:w-auto"
                            />
                        </div>
                    )}
                    {onStatCategoryChange && (
                        <div id="onboarding-scouting-stat-category" className="w-full md:w-auto">
                            <Select
                                value={statCategory}
                                onValueChange={(v) => onStatCategoryChange(v as ScoutingStatCategory)}
                            >
                                <SelectTrigger className="w-full md:w-fit bg-surface-container-highest border-0">
                                    <div className="flex items-center gap-2">
                                        <MaterialIcon name="filter_list" className="text-primary text-lg" />
                                        <SelectValue />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    {statCategories.map((category) => (
                                        <SelectItem key={category.id} value={category.id}>
                                            {category.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {onExportToExcel && (
                        <ExportToExcelButton
                            mutationFn={onExportToExcel}
                            label="Export to Excel"
                            successMessage="Scouting data exported successfully"
                            className="w-full md:w-auto"
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default ScoutingNavigation;
